class AcQuickAddOverlay extends HTMLElement {
  #events;
  #request;
  #generation = 0;
  #session = 0;
  #gallery;
  #galleryReady;
  #quantityCleanup;
  #images;
  #content;
  #retryUrl;
  #stableChoices = [];
  #submitting = false;
  #pending = false;

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    this.#content = this.querySelector('[data-ac-quick-add-content]');
    const { signal } = this.#events;
    this.addEventListener('change', this.#change, { signal });
    this.addEventListener('submit', this.#submit, { signal });
    this.querySelector('[data-ac-quick-add-retry]')?.addEventListener('click', () => {
      if (this.#retryUrl) this.#load(this.#retryUrl);
    }, { signal });
    document.addEventListener('ac:overlay-open', this.#open, { signal });
    document.addEventListener('ac:overlay-close', this.#close, { signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#reset();
  }

  #find(selector) { return this.querySelector(selector); }

  #disposeProduct() {
    this.#gallery?.destroy();
    this.#gallery = null;
    this.#quantityCleanup?.();
    this.#quantityCleanup = null;
    this.#images?.abort();
    const product = this.#find('[data-ac-quick-add-product]');
    if (product) window.AcLottieManager?.destroyWithin(product);
  }

  #reset() {
    this.#session++;
    this.#generation++;
    this.#request?.abort();
    this.#pending = false;
    this.#disposeProduct();
    this.#content?.removeAttribute('aria-busy');
    this.#status('');
  }

  #open = (event) => {
    if (event.detail?.name !== 'quick-add' || event.detail.panel !== this) return;
    const trigger = event.detail.trigger;
    const url = trigger?.dataset.productUrl || trigger?.href;
    if (!url) return;
    this.#reset();
    this.#find('[data-ac-quick-add-details]').href = url;
    this.#stableChoices = [];
    this.#content.replaceChildren(this.#find('[data-ac-quick-add-skeleton]').content.cloneNode(true));
    this.#content.scrollTop = 0;
    this.#find('[data-ac-quick-add-button-host]').replaceChildren();
    this.#find('[data-ac-quick-add-purchase]').hidden = true;
    // Prepare the existing gallery dependency while Shopify renders the product.
    this.#galleryReady = window.AcProductGallery.ensureSwiper({ script: this.dataset.swiperScript, stylesheet: this.dataset.swiperStylesheet });
    this.#galleryReady.catch(() => {});
    this.#load(url);
  };

  #close = (event) => {
    if (event.detail?.name !== 'quick-add' || event.detail.panel !== this) return;
    this.#reset();
  };

  #change = (event) => {
    const option = event.target.closest?.('[data-ac-quick-add-option-value]');
    if (!option || this.#submitting) return;
    const product = option.closest('[data-ac-quick-add-product]');
    const values = [...product.querySelectorAll('[data-ac-quick-add-option-value]:checked')].map((input) => input.value);
    if (!values.length) return;
    const url = new URL(option.dataset.productUrl || product.dataset.productUrl, location.origin);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', values.join(','));
    this.#load(url.href);
  };

  async #load(productUrl) {
    const generation = ++this.#generation;
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;
    const timeout = setTimeout(() => controller.abort(new Error('Quick add request timed out')), 30000);
    this.#pending = true;
    this.#retryUrl = productUrl;
    this.#error('');
    if (!this.#content.childElementCount) this.#content.append(this.#find('[data-ac-quick-add-skeleton]').content.cloneNode(true));
    this.#content.setAttribute('aria-busy', 'true');
    this.#find('[data-ac-quick-add-product]')?.setAttribute('aria-busy', 'true');
    this.#buttonState();
    this.#status(this.dataset.loadingLabel);
    try {
      const url = new URL(productUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const html = await this.#fetchProduct(url, controller.signal);
      if (generation !== this.#generation) return;
      // Inert parsing avoids loading images from a variant gallery we may discard.
      const template = document.createElement('template');
      template.innerHTML = html;
      const fresh = template.content.querySelector('[data-ac-quick-add-product]');
      if (!fresh?.querySelector('[data-ac-quick-add-variant-input]')) throw new Error('Invalid product response');
      this.#render(fresh);
    } catch (error) {
      if (generation !== this.#generation || error.name === 'AbortError') return;
      console.warn('[AcQuickAdd] Product could not load', error);
      // Keep the last confirmed variant visible and purchasable; retry retains the failed query.
      for (const input of this.querySelectorAll('[data-ac-quick-add-option-value]')) {
        input.checked = this.#stableChoices.includes(input.value);
      }
      const product = this.#find('[data-ac-quick-add-product]');
      if (product) window.AcLottieManager?.refresh(product);
      else this.#content.replaceChildren();
      this.#error(this.dataset.errorLabel, true);
    } finally {
      clearTimeout(timeout);
      if (generation === this.#generation) {
        this.#pending = false;
        this.#content.removeAttribute('aria-busy');
        this.#find('[data-ac-quick-add-product]')?.removeAttribute('aria-busy');
        this.#status('');
        this.#buttonState();
      }
    }
  }

  async #fetchProduct(url, signal) {
    // One retry for a transient GET failure; never retry closing or superseded requests.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetch(url, { headers: { Accept: 'text/html' }, signal });
        if (response.ok) return await response.text();
        if (attempt === 0 && [502, 503, 504].includes(response.status)) continue;
        throw new Error(`Quick add response: HTTP ${response.status}`);
      } catch (error) {
        if (signal.aborted || attempt > 0 || error.name !== 'TypeError') throw error;
      }
    }
  }

  #render(fresh) {
    const current = this.#find('[data-ac-quick-add-product]');
    const sameProduct = current?.dataset.productId === fresh.dataset.productId;
    const previousMedia = current?.dataset.activeMediaId;
    if (sameProduct) {
      Object.assign(current.dataset, fresh.dataset);
      current.querySelector('[data-ac-quick-add-price]').replaceChildren(
        ...fresh.querySelector('[data-ac-quick-add-price]').childNodes);
      for (const freshGroup of fresh.querySelectorAll('[data-ac-quick-add-option-position]')) {
        const group = [...current.querySelectorAll('[data-ac-quick-add-option-position]')]
          .find((item) => item.dataset.acQuickAddOptionPosition === freshGroup.dataset.acQuickAddOptionPosition);
        if (!group) continue;
        const inputs = [...group.querySelectorAll('input')];
        const nextInputs = [...freshGroup.querySelectorAll('input')];
        if (inputs.length !== nextInputs.length || inputs.some((input, i) => input.value !== nextInputs[i].value)) {
          window.AcLottieManager?.destroyWithin(group);
          group.replaceWith(freshGroup);
          continue;
        }
        inputs.forEach((input, index) => {
          const next = nextInputs[index];
          input.checked = next.checked;
          input.disabled = next.disabled;
          input.dataset.productUrl = next.dataset.productUrl;
          if (next.disabled) input.nextElementSibling.dataset.unavailable = 'true';
          else delete input.nextElementSibling.dataset.unavailable;
        });
      }
      current.querySelector('[data-ac-quick-add-variant-input]').value = fresh.dataset.selectedVariantId;
      const stock = current.querySelector('[data-ac-quick-add-stock]');
      const nextStock = fresh.querySelector('[data-ac-quick-add-stock]');
      if (stock && nextStock) { stock.textContent = nextStock.textContent; stock.dataset.available = nextStock.dataset.available; }
      if (previousMedia !== fresh.dataset.activeMediaId) this.#gallery?.selectMedia(fresh.dataset.activeMediaId);
    } else {
      this.#disposeProduct();
      this.#content.replaceChildren(fresh);
      this.#content.scrollTop = 0;
      this.#quantityCleanup = window.AcProductQuantity.bind(
        fresh.querySelectorAll('[data-ac-product-quantity-control]'), (value) => this.#quantity(value));
      this.#images = new AbortController();
      for (const image of fresh.querySelectorAll('img')) {
        if (image.complete) continue;
        image.dataset.loading = '';
        const ready = () => { delete image.dataset.loading; };
        image.addEventListener('load', ready, { once: true, signal: this.#images.signal });
        image.addEventListener('error', ready, { once: true, signal: this.#images.signal });
      }
      this.#initGallery(fresh, this.#session);
    }
    const product = this.#find('[data-ac-quick-add-product]');
    const host = this.#find('[data-ac-quick-add-button-host]');
    const template = fresh.querySelector('[data-ac-quick-add-footer]');
    const nextButton = template.content.querySelector('button');
    const button = host.querySelector('button');
    if (sameProduct && button) {
      button.toggleAttribute('data-variant-unavailable', nextButton.hasAttribute('data-variant-unavailable'));
      button.querySelector('[data-ac-quick-add-submit-label]').textContent = nextButton.textContent.trim();
    } else host.replaceChildren(template.content.cloneNode(true));
    this.#find('[data-ac-quick-add-purchase]').hidden = false;
    this.#stableChoices = [...product.querySelectorAll('[data-ac-quick-add-option-value]:checked')].map((input) => input.value);
    this.#find('[data-ac-quick-add-details]').href = product.dataset.detailsUrl;
    this.#quantity(sameProduct ? product.querySelector('[name="quantity"]')?.value : product.dataset.quantityMin);
    window.AcLottieManager?.refresh(product);
  }

  async #initGallery(product, session) {
    const scope = product.querySelector('[data-ac-quick-add-gallery]');
    const slides = [...scope.querySelectorAll('[data-media-id]')];
    if (slides.length > 1) {
      try {
        await this.#galleryReady;
      } catch {
        if (session !== this.#session || !product.isConnected) return;
        // Native horizontal scrolling remains usable if the shared library cannot load.
        scope.dataset.nativeGallery = '';
      }
    }
    if (session !== this.#session || !product.isConnected) return;
    this.#gallery = window.AcProductGallery.create({
      scope, stage: scope.querySelector('[data-ac-quick-add-media]'),
      track: scope.querySelector('[data-ac-quick-add-track]'), slides,
      previous: scope.querySelector('[data-ac-quick-add-previous]'),
      next: scope.querySelector('[data-ac-quick-add-next]'),
      current: scope.querySelector('[data-ac-quick-add-current]'),
      initialMediaId: product.dataset.activeMediaId,
    });
  }

  #quantity(value) {
    const product = this.#find('[data-ac-quick-add-product]');
    const input = product?.querySelector('[name="quantity"]');
    if (!input) return;
    const rule = { min: Number(product.dataset.quantityMin) || 1,
      max: product.dataset.quantityMax ? Number(product.dataset.quantityMax) : null,
      increment: Number(product.dataset.quantityStep) || 1 };
    input.min = rule.min;
    input.step = rule.increment;
    if (rule.max === null) input.removeAttribute('max');
    else input.max = rule.max;
    input.value = window.AcProductQuantity.normalize(value, rule);
    const minus = product.querySelector('[data-ac-quantity-minus]');
    const plus = product.querySelector('[data-ac-quantity-plus]');
    if (minus) minus.disabled = Number(input.value) <= rule.min;
    if (plus) plus.disabled = window.AcProductQuantity.normalize(Number(input.value) + rule.increment, rule) === Number(input.value);
    const rules = product.querySelector('[data-ac-product-quantity-rules]');
    if (rules) rules.textContent = [
      rule.min > 1 ? this.dataset.quantityMinLabel?.replace('[quantity]', rule.min) : '',
      rule.max !== null ? this.dataset.quantityMaxLabel?.replace('[quantity]', rule.max) : '',
      rule.increment > 1 ? this.dataset.quantityStepLabel?.replace('[quantity]', rule.increment) : '',
    ].filter(Boolean).join(' · ');
  }

  #buttonState() {
    const button = this.#find('[data-ac-quick-add-submit]');
    if (!button) return;
    button.disabled = this.#pending || this.#submitting || button.hasAttribute('data-variant-unavailable');
  }

  #error(message, retry = false) {
    const error = this.#find('[data-ac-quick-add-error]');
    error.textContent = message;
    error.hidden = !message;
    this.#find('[data-ac-quick-add-retry]').hidden = !message || !retry;
    if (message) this.#find('[data-ac-quick-add-purchase]').hidden = false;
  }

  #submit = async (event) => {
    const form = event.target.closest?.('[data-ac-quick-add-form]');
    if (!form) return;
    event.preventDefault();
    const button = this.#find('[data-ac-quick-add-submit]');
    if (!button || button.disabled || this.#pending || this.#submitting) return;
    const session = this.#session;
    this.#submitting = true;
    this.#quantity(form.querySelector('[name="quantity"]')?.value);
    this.#buttonState();
    this.#error('');
    const label = button.querySelector('[data-ac-quick-add-submit-label]');
    const originalLabel = label.textContent;
    label.textContent = this.dataset.addingLabel;
    button.classList.add('is-loading');
    form.setAttribute('aria-busy', 'true');
    const options = [...form.querySelectorAll('[data-ac-quick-add-option-value]:not(:disabled)')];
    options.forEach((input) => { input.disabled = true; });
    try {
      if (!window.AcCartActions) throw new Error(this.dataset.errorLabel);
      const item = await window.AcCartActions.add(form);
      const receipt = await window.AcCartActions.sync(item, { forOpen: true });
      window.AcCartActions.announce(this.dataset.addedLabel);
      if (session === this.#session) window.AcCartActions.open(receipt);
    } catch (error) {
      if (session === this.#session) {
        this.#error(error.message || this.dataset.errorLabel);
        this.#find('[data-ac-quick-add-error]').focus({ preventScroll: true });
      }
    } finally {
      this.#submitting = false;
      options.forEach((input) => { input.disabled = false; });
      form.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      label.textContent = originalLabel;
      this.#buttonState();
    }
  };

  #status(message) {
    const status = this.#find('[data-ac-quick-add-status]');
    if (status) { status.textContent = message || ''; status.hidden = !message; }
  }
}
if (!customElements.get('ac-quick-add-overlay')) customElements.define('ac-quick-add-overlay', AcQuickAddOverlay);
